import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, RotateCcw, X, Check, FileText } from "lucide-react";

// Item 1 (global upload pop-up rework) — a single reusable pattern used by
// every file-upload location in the app (Sales / Operations / Finance).
// Historically every upload site fired its API call the instant a file was
// chosen (`<input type="file" onChange={uploadNow}>`). That is no longer
// allowed: a chosen file must be staged locally and only actually submitted
// (uploaded) when the user clicks Submit. Cancel discards the staged file
// without any API call and never touches an existing submitted document.
// Re-Upload re-opens the picker and replaces the staged file — the old
// staged file is discarded, nothing is ever uploaded until Submit.
//
// This is a hook (state/behavior) + a tiny presentational bar, not a single
// monolithic dialog, precisely so it can be dropped into both inline row
// contexts (Workspace.jsx's many per-blocker upload buttons, JobDetail's
// document lists) and inside larger existing dialogs (AutoFileDialog,
// CustomerOnboardingDialog) without forcing a specific container shape.
export function useFileSelectConfirm(onSubmit) {
  const inputRef = useRef(null);
  const [pending, setPending] = useState(null); // staged File, not yet uploaded
  const [busy, setBusy] = useState(false);

  const openPicker = () => inputRef.current?.click();

  // Wire this to the hidden <input type="file">'s onChange. It never
  // uploads — it only stages the selection (or replaces a prior staged
  // selection when used as the Re-Upload trigger).
  const onFileChosen = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) setPending(file);
  };

  const cancel = () => { if (!busy) setPending(null); };

  // Re-Upload = open the picker again; onFileChosen above replaces the
  // pending file when a new one is chosen. The previously staged (never
  // submitted) file is simply dropped in favor of the new selection.
  const reUpload = () => { if (!busy) openPicker(); };

  const submit = async () => {
    if (!pending || busy) return;
    setBusy(true);
    try {
      await onSubmit(pending);
      setPending(null);
    } catch {
      // Caller is responsible for surfacing the error (toast, etc). The
      // staged file is kept so the user can retry Submit or Cancel/Re-Upload.
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setPending(null); setBusy(false); };

  return { inputRef, pending, busy, openPicker, onFileChosen, cancel, reUpload, submit, reset };
}

export function FileSelectConfirmBar({ pending, busy, onSubmit, onCancel, onReUpload, submitLabel = "Submit", className = "", showSubmit = true }) {
  if (!pending) return null;
  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50/70 px-2.5 py-2 ${className}`} data-testid="file-confirm-bar">
      <FileText className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
      <span className="text-xs font-medium text-indigo-900 truncate max-w-[14rem]" title={pending.name}>{pending.name}</span>
      <div className="ml-auto flex items-center gap-1.5">
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={onReUpload} data-testid="file-confirm-reupload">
          <RotateCcw className="h-3 w-3 mr-1" />Re-Upload
        </Button>
        <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" disabled={busy} onClick={onCancel} data-testid="file-confirm-cancel">
          <X className="h-3 w-3 mr-1" />Cancel
        </Button>
        {showSubmit && (
          <Button type="button" size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" disabled={busy} onClick={onSubmit} data-testid="file-confirm-submit">
            {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}{submitLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
